import React, { useEffect, useRef, useState, useCallback } from "react";
import Container from "../../../layouts/containers/Container";
import InvoiceTreatment from "./components/InvoiceTreatment";
import baseUrl from "../../../api/baseUrl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import InvoicePatient from "./components/InvoicePatient";
import Amount from "./components/Amount";
import Pays from "./components/Pays";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { showSmsResultToast, notifySmsBalanceChanged } from "../../../utils/sms/smsToast";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../redux/loader/loaderActions";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  setPrintaction,
  setPrintAmount,
  setPrintclinic,
  setPrintPatient,
  setPrintSubTreatment,
  setPrintTreatment,
  setPrintTemplate2action,
  setPrintTemplate2Amount,
  setPrintTemplate2clinic,
  setPrintTemplate2Patient,
  setPrintTemplate2SubTreatment,
  setPrintTemplate2Treatment,
} from "../../../redux/printstate/printActions";
import { useInvoicePrintTemplate } from "../../../context/InvoicePrintTemplateContext";
import PaymentTr from "./components/PaymentTr";
import ReportModal from "../../manage/treatmenttable/components/reportModal/ReportModal";
import Printtest from "../../setting/manageprint/printtest/Printtest";
import getDecryptedToken from "../../../api/getDecryptedToken";

const SMS_DEPLETED_MESSAGE =
  "SMS tokens finished — buy a package from My Account.";
interface FormData {
  treatmentId: string;
  patientId: string | null;
  userName: string;
  regno: string;
  paymentid: string;
  age: number;
  total: number;
  lastDue: number;
  payable: number;
  discount: number;
  netotal: number;
  due: number;
  card: number;
  cash: number;
  bankTransfer: number;
}

const getPaymentTreatmentTotal = (paymentId: string, treatments: any[]) => {
  let total = 0;
  const paymentIdStr = String(paymentId);

  treatments.forEach((treatmentItem: any) => {
    const itemPaymentId =
      treatmentItem.paymentId?._id ?? treatmentItem.paymentId;
    if (
      String(itemPaymentId) === paymentIdStr &&
      isBillableTreatment(treatmentItem)
    ) {
      total += parseFloat(treatmentItem.price) || 0;
    }

    treatmentItem?.subTreatment?.forEach((sub: any) => {
      const subPaymentId = sub.paymentId?._id ?? sub.paymentId;
      if (
        String(subPaymentId) === paymentIdStr &&
        isPricedSubTreatment(sub)
      ) {
        total += parseFloat(sub.price) || 0;
      }
    });
  });

  return total;
};

const getPaymentItemTotal = (item: any) =>
  (parseFloat(item?.due) || 0) +
  (parseFloat(item?.cash) || 0) +
  (parseFloat(item?.card) || 0) +
  (parseFloat(item?.bankTransfer) || 0) +
  (parseFloat(item?.discount) || 0);

const isPricedSubTreatment = (subItem: any) =>
  subItem?.selected_option === "treatment_paid" ||
  subItem?.selected_option === "treatment_stage";

const isLongTermTreatment = (item: any) => item?.status === "long term";

const isBillableTreatment = (item: any) =>
  item?.status !== "planned" && !isLongTermTreatment(item);

const isUnpaidBillableSubTreatment = (subItem: any) =>
  isPricedSubTreatment(subItem) &&
  !subItem?.paid &&
  subItem?.status !== "long term";

const isVisibleSubTreatment = (subItem: any) => {
  if (subItem?.status === "long term") return false;
  if (isPricedSubTreatment(subItem)) return true;
  return subItem?.selected_option === "treatment_non_paid";
};

const hasUnpaidBillableItems = (treatments: any[]) =>
  treatments.some((item) => {
    if (!item?.paid && isBillableTreatment(item)) return true;
    return item?.subTreatment?.some((sub: any) =>
      isUnpaidBillableSubTreatment(sub)
    );
  });

const formatSubTreatmentLabel = (items: any) => {
  const parts = [items.treatment || "N/A"];
  if (items.type) parts.push(items.type);
  if (items.subtype) parts.push(items.subtype);
  if (items.stage) parts.push(items.stage);
  return parts.join(" ");
};

const getVisibleSubTreatments = (item: any) =>
  item?.subTreatment?.filter((sub: any) => isVisibleSubTreatment(sub)) ?? [];

/** Long-term estimate (full price) and remaining due after billed stages/subs. */
const getLongTermPrintSummary = (treatments: any[]) =>
  (treatments ?? []).reduce(
    (acc, item) => {
      if (!isLongTermTreatment(item)) return acc;
      const estimate = parseFloat(item?.price) || 0;
      const billed = (item?.subTreatment ?? []).reduce((subSum: number, sub: any) => {
        if (!isPricedSubTreatment(sub)) return subSum;
        return subSum + (parseFloat(sub?.price) || 0);
      }, 0);
      return {
        estimation: acc.estimation + estimate,
        longTermDue: acc.longTermDue + Math.max(0, estimate - billed),
      };
    },
    { estimation: 0, longTermDue: 0 }
  );

const invoiceCell =
  "border border-gray-400 dark:border-gray-500 px-4 py-3";
const invoiceCellRow =
  "border border-gray-400 dark:border-gray-500 border-b-2 border-b-gray-500 dark:border-b-gray-400 px-4 py-3";
const invoiceCellGroupEnd =
  "border border-gray-400 dark:border-gray-500 border-b-[3px] border-b-gray-600 dark:border-b-gray-300 px-4 py-3";
const invoiceHeadCell =
  "border border-gray-500 dark:border-gray-400 bg-slate-200 dark:bg-slate-600 px-4 py-3";

const Invoice: React.FC = () => {
  const { template: printTemplate } = useInvoicePrintTemplate();
  const [addTreatmentOpent, setAddTreatmentOpent] = useState(false);
  const [editTreatmentItem, setEditTreatmentItem] = useState<any>(null);
  const [reportOpen, setReportOpen] = useState(false);

  const [pays, setPays] = useState(false);
  const [edit, setEdit] = useState(false);
  const [smsDisabled, setSmsDisabled] = useState(false);
  const [treatment, setTreatment] = useState<any[]>([]);
  const [payment, setPayment] = useState<any[]>([]);
  const { ID } = useParams<{ ID: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = getDecryptedToken("role") === "Super Admin";
  const canEditInvoiceTreatment = useCallback(
    (item: any) => Boolean(item) && (!item?.paid || isSuperAdmin),
    [isSuperAdmin]
  );
  const [formData, setFormData] = useState<FormData>({
    treatmentId: ID || "",
    patientId: "",
    userName: "",
    regno: "",
    paymentid: "",
    age: 0,
    total: 0,
    lastDue: 0,
    payable: 0,
    discount: 0,
    netotal: 0,
    due: 0,
    card: 0,
    cash: 0,
    bankTransfer: 0,
  });

  const printRef = useRef<HTMLDivElement>(null);
  const [printData, setPrintData] = useState("");
  const [doctorId, setDoctorId] = useState("");

  const fetchData = useCallback(async () => {
    if (ID) {
      dispatch(setloadeActive());
      try {
        const treatmentResponse = await baseUrl.get(
          "treatment/find-treatment",
          {
            headers: { treatmentid: ID },
          }
        );
        const birthdateDate = new Date(
          treatmentResponse.data.data.patientId.dob ?? new Date()
        );

        setDoctorId(
          treatmentResponse?.data?.data?.doctorId?._id
            ? treatmentResponse?.data?.data?.doctorId?._id
            : treatmentResponse?.data?.data?.createdBy
        );

        const currentDate = new Date();
        const ageDiff = currentDate.getFullYear() - birthdateDate.getFullYear();
        const isBirthdayPassed =
          currentDate.getMonth() < birthdateDate.getMonth() ||
          (currentDate.getMonth() === birthdateDate.getMonth() &&
            currentDate.getDate() < birthdateDate.getDate());

        setTreatment(treatmentResponse.data.data?.treatment || []);
        const treatmentData = treatmentResponse.data.data;

        let totals = 0;

        treatmentResponse.data.data.treatment.forEach((item: any) => {
          if (!item.paid && isBillableTreatment(item)) {
            totals += parseFloat(item.price);
          }

          item?.subTreatment?.forEach((items: any) => {
            if (isUnpaidBillableSubTreatment(items)) {
              totals += parseFloat(items.price) || 0;
            }
          });
        });

        const paymentResponse = await baseUrl.get("payment/last-payment", {
          headers: { treatmentid: ID },
        });

        dispatch(setloadeDeactive());
        setPayment(paymentResponse.data.data);

        let dues = 0;
        paymentResponse.data.data.forEach((item: any) => {
          const paymentTotal = getPaymentTreatmentTotal(
            item._id,
            treatmentResponse.data.data.treatment
          );
          dues +=
            paymentTotal -
            parseFloat(item.discount) -
            parseFloat(item.card) -
            parseFloat(item.bankTransfer) -
            parseFloat(item.cash);
        });

        setFormData((prevFormData) => ({
          ...prevFormData,
          total: totals,
          regno: treatmentData.patientId.regNo,
          userName: treatmentData.patientId.fullName,
          treatmentId: treatmentData?._id,
          patientId: treatmentData?.patientId._id,
          age: ageDiff - (isBirthdayPassed ? 1 : 0),
          lastDue: dues,
          payable: dues + totals,
          netotal: dues + totals,
          due: dues + totals,
          paymentid: "",
          discount: 0,
          card: 0,
          cash: 0,
          bankTransfer: 0,
        }));
      } catch (error) {
        dispatch(setloadeDeactive());
        console.error(error);
      }
    }
  }, [ID, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const smsFlag = getDecryptedToken("sms");
    if (smsFlag !== "true" && smsFlag !== "1") {
      setSmsDisabled(false);
      return;
    }

    baseUrl
      .get("/client/account/sms-alert", { params: { refresh: "false" } })
      .then((response) => {
        const data = response?.data?.data;
        const depleted =
          data?.level === "depleted" ||
          (data?.tokenCredits != null && data.tokenCredits <= 0) ||
          (data?.totalAvailable != null && data.totalAvailable <= 0);
        setSmsDisabled(Boolean(depleted));
      })
      .catch(() => setSmsDisabled(false));
  }, []);

  useEffect(() => {
    if (!ID) {
      setTreatment([]);
      setPayment([]);

      const state = location.state as {
        patientId?: string;
        userName?: string;
        regno?: string;
        dob?: string;
      } | null;

      let age = 0;
      if (state?.dob) {
        const birthdateDate = new Date(state.dob);
        const currentDate = new Date();
        const ageDiff = currentDate.getFullYear() - birthdateDate.getFullYear();
        const isBirthdayPassed =
          currentDate.getMonth() < birthdateDate.getMonth() ||
          (currentDate.getMonth() === birthdateDate.getMonth() &&
            currentDate.getDate() < birthdateDate.getDate());
        age = ageDiff - (isBirthdayPassed ? 1 : 0);
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        treatmentId: "",
        patientId: state?.patientId || "",
        userName: state?.userName || "",
        age,
        regno: state?.regno || "",
        total: 0,
        lastDue: 0,
        payable: 0,
        discount: 0,
        netotal: 0,
        due: 0,
        card: 0,
        cash: 0,
        bankTransfer: 0,
      }));
    }
  }, [location, ID]);

  const addToTreatment = useCallback((e: any) => {
    if (ID) {
      fetchData();
    } else if (e?.forEach) {
      let totals = 0;
      e.forEach((item: any) => {
        if (isBillableTreatment(item)) {
          totals +=
            parseFloat(item.price) ||
            (parseFloat(item.prices) || 0) * (parseFloat(item.quantity) || 1);
        }
        item?.subTreatment?.forEach((items: any) => {
          if (isPricedSubTreatment(items)) {
            totals += parseFloat(items.price) || 0;
          }
        });
      });

      setFormData((prevFormData) => ({
        ...prevFormData,
        total: totals,
        payable: totals,
        netotal: totals,
        due: totals,
      }));
       }
  }, [ID, fetchData]);

  const dispatchPrintActions = useCallback(
    (e: any) => {
      const isTemplate2 = printTemplate === "printTemplate_2";
      const setAction = isTemplate2 ? setPrintTemplate2action : setPrintaction;
      const setClinic = isTemplate2 ? setPrintTemplate2clinic : setPrintclinic;
      const setPatient = isTemplate2 ? setPrintTemplate2Patient : setPrintPatient;
      const setAmount = isTemplate2 ? setPrintTemplate2Amount : setPrintAmount;
      const setTreatment = isTemplate2 ? setPrintTemplate2Treatment : setPrintTreatment;
      const setSubTreatment = isTemplate2
        ? setPrintTemplate2SubTreatment
        : setPrintSubTreatment;

      const layoutFields = isTemplate2
        ? e?.amounts?.clinicId?.printTemplate_2
        : e?.amounts?.clinicId?.bills;

      layoutFields?.forEach((element: any) => {
        const { title, top, left, show, size, height, width } = element;
        if (top) dispatch(setAction({ name: title, target: "top", value: top }));
        if (left) dispatch(setAction({ name: title, target: "left", value: left }));
        if (show !== undefined)
          dispatch(setAction({ name: title, target: "show", value: show }));
        if (size) dispatch(setAction({ name: title, target: "size", value: size }));
        if (height)
          dispatch(setAction({ name: title, target: "height", value: height }));
        if (width) dispatch(setAction({ name: title, target: "width", value: width }));
      });

      const clinicPayload = {
        clinicName: e?.amounts?.clinicId?.title,
        tpNo: e?.amounts?.clinicId?.tpNo,
        address: e?.amounts?.clinicId?.address,
        drName:
          "Dr " +
          e?.amounts?.doctorId?.firstName +
          " " +
          e?.amounts?.doctorId?.lastName,
        printImages: e?.amounts?.clinicId?.printImages,
        headerImages: e?.amounts?.clinicId?.headerImages,
        footerImages: e?.amounts?.clinicId?.footerImages,
      };
      dispatch(setClinic(clinicPayload));

      dispatch(
        setPatient({
          invoiceNo: e?.amounts?.invoiceNo,
          invoiceDate: e?.amounts?.updatedAt,
          regNo: e?.patientData?.regNo,
          fullName: e?.patientData?.fullName,
          mobileNo: e?.patientData?.mobileNo,
          gender: e?.patientData?.gender,
          dob: e?.patientData?.dob,
        })
      );
      const { estimation, longTermDue } = getLongTermPrintSummary(treatment);

      dispatch(
        setAmount({
          total: e?.amounts?.total,
          lastDue: e?.amounts?.lastDue,
          discount: e?.amounts?.discount,
          netotal: e?.amounts?.netotal,
          card: e?.amounts?.card,
          cash: e?.amounts?.cash,
          bankTransfer: e?.amounts?.bankTransfer,
          longTermDue,
          estimation,
        })
      );
      dispatch(
        setTreatment(
          e?.treatmentdata?.filter(
            (item: any) => item?.status !== "long term"
          ) ?? []
        )
      );
      dispatch(
        setSubTreatment(
          e?.subPayments?.filter((sub: any) => sub?.status !== "long term") ?? []
        )
      );
    },
    [dispatch, printTemplate, treatment]
  );

  const handlePrint = useCallback(
    async (e: any) => {
      try {
        const response = await baseUrl.get("payment/getPaymentById", {
          headers: { paymentid: e?.paymentid },
        });
        console.log(e?.paymentid);
        setPrintData(response.data);

        dispatchPrintActions(response.data);
      } catch (error) {
        console.log(error);
      }
    },
    [dispatchPrintActions]
  );

  const handleSave = useCallback(
    (e: any,print?:boolean) => {
      dispatch(setloadeActive());
      if (!edit) {
        if (ID) {
          baseUrl
            .post("payment/addPayment", {
              ...formData,
              ...(doctorId ? { doctorId } : {}),
              sms: e,
            })
            .then((response) => {
              dispatch(setloadeDeactive());
              setFormData({
                ...formData,
                card: 0,
                cash: 0,
                bankTransfer: 0,
                discount: 0,
              });
              setPays(false);
              setEdit(false);
              fetchData();
              toast("Payment saved successfully");
              showSmsResultToast(response.data?.sms);
              notifySmsBalanceChanged();
              if (print) {
                handlePrint({ paymentid: response.data.data._id });
              }

            })
            .catch((error) => {
              dispatch(setloadeDeactive());
              toast(
                error?.response?.data?.message || "Failed to save payment"
              );
            });
        } else {
          if (formData.patientId !== "") {
            baseUrl
              .post("treatment/create-paymenttreatments", {
                patientId: formData.patientId,
                treatment,
                formData,
                ...(doctorId ? { doctorId } : {}),
                sms: e,
              })
              .then((response) => {
                dispatch(setloadeDeactive());
                setFormData({
                  ...formData,
                  card: 0,
                  cash: 0,
                  discount: 0,
                  bankTransfer: 0,
                });
                setPays(false);
                setEdit(false);
                fetchData();
                toast("Payment saved successfully");
                showSmsResultToast(response.data?.sms);
                notifySmsBalanceChanged();

                navigate(`/client/payments/invoice/${response.data.data.treatmentId}`);
                if (print) {
                  handlePrint({ paymentid: response.data.data._id });
                }
              })
              .catch((error) => {
                dispatch(setloadeDeactive());
                toast(
                  error?.response?.data?.message || "Failed to save payment"
                );
              });
          } else {
            toast("select Patient");
          }
        }
      } else {
        baseUrl
          .patch(
            "payment/editPayment",
            {
              ...formData,
            },
            {
              headers: {
                paymentid: formData?.paymentid,
              },
            }
          )
          .then((response) => {
            dispatch(setloadeDeactive());
            setFormData({
              ...formData,
              card: 0,
              cash: 0,
              discount: 0,
              bankTransfer: 0,
            });
            setPays(false);
            fetchData();
            setEdit(false);
            navigate(`/client/payments/invoice/${response.data.data.treatmentId}`);
            if (print) {
              handlePrint({ paymentid: response.data.data._id });
            }
          })
          .catch((error) => {
            dispatch(setloadeDeactive());
            toast(
              error?.response?.data?.message || "Failed to update payment"
            );
          });
      }
    },
    [
      ID,
      formData,
      treatment,
      dispatch,
      navigate,
      doctorId,
      edit,
      fetchData,
      handlePrint,
    ]
  );

  const handlePays = useCallback(() => {
    if (formData.patientId === "") {
      toast("select Patient");
      return;
    }

    if (formData.payable <= 0 && !hasUnpaidBillableItems(treatment)) {
      toast("Nothing to pay");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      paymentid: "",
      discount: 0,
      card: 0,
      cash: 0,
      bankTransfer: 0,
      netotal: prev.payable,
      due: prev.payable,
    }));
    setPays(true);
    setEdit(false);
  }, [formData.patientId, formData.payable, treatment]);

  const handleEdit = useCallback((e: any) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      netotal: parseFloat(e?.netotal) || 0,
      payable: (parseFloat(e?.netotal) || 0) + (parseFloat(e?.discount) || 0),
      due: parseFloat(e?.due) || 0,
      paymentid: e?._id,
      discount: parseFloat(e?.discount) || 0,
      card: parseFloat(e?.card) || 0,
      cash: parseFloat(e?.cash) || 0,
      bankTransfer: parseFloat(e?.bankTransfer) || 0,
    }));
    setPays(true);
    setEdit(true);
  }, []);

  const removeDiagnosis = useCallback(
    (e: any) => {
      if (ID) {
        baseUrl
          .post(
            "treatment/remove-treatments",
            {},
            { headers: { treatmentid: ID, treatmentsid: e } }
          )
          .then(async () => {
            const updatedItems = treatment.filter((item) => item?._id !== e);
            setTreatment(updatedItems);
            let totals = 0;
            updatedItems?.forEach((item: any) => {
              if (!item?.paid && isBillableTreatment(item)) {
                totals += parseFloat(item.price) * parseFloat(item.quantity);
              }

              item?.subTreatment?.forEach((items: any) => {
                if (isUnpaidBillableSubTreatment(items)) {
                  totals += parseFloat(items.price) || 0;
                }
              });
            });

            const paymentResponse = await baseUrl.get("payment/last-payment", {
              headers: { treatmentid: ID },
            });

            dispatch(setloadeDeactive());
            setPayment(paymentResponse.data.data);

            let dues = 0;
            paymentResponse.data.data.forEach((item: any) => {
              const paymentTotal = getPaymentTreatmentTotal(
                item._id,
                updatedItems
              );
              dues +=
                paymentTotal -
                parseFloat(item.discount) -
                parseFloat(item.card) -
                parseFloat(item.bankTransfer) -
                parseFloat(item.cash);
            });

            setFormData((prevFormData) => ({
              ...prevFormData,
              total: totals,
              lastDue: dues,
              payable: dues + totals,
              netotal: dues + totals,
              due: dues + totals,
            }));
          })
          .catch((error) => console.log(error));
      } else {
        const updatedItems = treatment.filter(
          (item) => item?.treatmentkey !== e
        );
        setTreatment(updatedItems);
        let totals = 0;
        updatedItems.forEach((item: any) => {
          if (isBillableTreatment(item)) {
            totals += parseFloat(item.price) * parseFloat(item.quantity);
          }
          item?.subTreatment?.forEach((items: any) => {
            if (isPricedSubTreatment(items)) {
              totals += parseFloat(items.price) || 0;
            }
          });
        });

        setFormData((prevFormData) => ({
          ...prevFormData,
          total: totals,
          payable: totals,
          netotal: totals,
        }));
      }
    },
    [ID, treatment, dispatch]
  );
  const removeSubTreatment = useCallback(
    (e: any) => {
      if (ID) {
        baseUrl
          .patch("/client/treatments/subTreatment/delete-subtreetment", {
            maintreatmentid: ID,
            treatmentid: e.treatment_id,
            subtreatmentid: e.sub_treatment_id,
          })

          .then(async () => {
            const updatedItems = treatment.map((item) => {
              if (item._id === e?.treatment_id) {
                return {
                  ...item,
                  subTreatment: item.subTreatment.filter(
                    (subItem: any) => subItem._id !== e?.sub_treatment_id
                  ),
                };
              }
              return item;
            });
            setTreatment(updatedItems);
            let totals = 0;

            updatedItems.forEach((item: any) => {
              if (!item?.paid && isBillableTreatment(item)) {
                totals += parseFloat(item.price) * parseFloat(item.quantity);
              }

              item?.subTreatment?.forEach((items: any) => {
                if (isUnpaidBillableSubTreatment(items)) {
                  totals += parseFloat(items.price) || 0;
                }
              });
            });

            const paymentResponse = await baseUrl.get("payment/last-payment", {
              headers: { treatmentid: ID },
            });

            dispatch(setloadeDeactive());
            setPayment(paymentResponse.data.data);

            let dues = 0;
            paymentResponse.data.data.forEach((item: any) => {
              const paymentTotal = getPaymentTreatmentTotal(
                item._id,
                updatedItems
              );
              dues +=
                paymentTotal -
                parseFloat(item.discount) -
                parseFloat(item.card) -
                parseFloat(item.bankTransfer) -
                parseFloat(item.cash);
            });

            setFormData((prevFormData) => ({
              ...prevFormData,
              total: totals,
              lastDue: dues,
              payable: dues + totals,
              netotal: dues + totals,
              due: dues + totals,
            }));
          })
          .catch((error) => console.log(error));
      } else {
        const updatedItems = treatment.filter(
          (item) => item?.treatmentkey !== e
        );
        setTreatment(updatedItems);
        let totals = 0;
        updatedItems.forEach((item: any) => {
          if (isBillableTreatment(item)) {
            totals += parseFloat(item.prices) * parseFloat(item.quantity);
          }
        });

        setFormData((prevFormData) => ({
          ...prevFormData,
          total: totals,
          payable: totals,
          netotal: totals,
        }));
      }
    },
    [ID, treatment, dispatch]
  );

  useEffect(() => {
    if (printData) {
      handlePrinting();
    }
  }, [printData]);

  const handlePrinting = async () => {
    if (printRef.current) {
      printRef.current.style.display = "block";
      const element = printRef.current;
      const options = {
        scale: 2, // Improve quality
        useCORS: true, // Allow cross-origin images
        allowTaint: true,
      };

      const canvas = await html2canvas(element, options);
      const imgData = canvas.toDataURL("image/png");

      const imgWidth = 210; // A4 width in mm
      const imgProps = canvas.width / canvas.height;
      const imgHeight = imgWidth / imgProps; // Auto height based on aspect ratio

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: [imgWidth, imgHeight], // Dynamic height
      });

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST");

      const pdfBlob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);

      // Create an iframe and append to document
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.top = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      printRef.current.style.transform = "none";
      document.body.appendChild(iframe);
      iframe.src = blobUrl;

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          printRef.current!.style.display = "none";
        }, 500);
      };
    } else {
      console.error("Content ref is not defined");
    }
  };

  const handleSms = useCallback(async (e: any) => {
    if (smsDisabled) {
      toast(SMS_DEPLETED_MESSAGE);
      return;
    }
    try {
      const response = await baseUrl.get("payment/smsinvoice", {
        headers: { paymentid: e?._id, patientid: e?.patientId },
      });
      showSmsResultToast(response.data?.sms);
      notifySmsBalanceChanged();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Could not send SMS. Check your SMS balance and try again.";
      toast.error(message);
    }
  }, [smsDisabled]);

  const handleDelete = useCallback(async (paymentItem: any) => {
    try {
      // Confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to delete payment invoice ${paymentItem?.invoiceNo}? This action cannot be undone.`
      );

      if (!confirmed) {
        return;
      }

      dispatch(setloadeActive());
      await baseUrl.delete("payment/deletePaymentById", {
        headers: { paymentid: paymentItem?._id },
      });

      toast("Payment deleted successfully");

      // Refresh data after deletion
      await fetchData();

      dispatch(setloadeDeactive());
    } catch (error: any) {
      dispatch(setloadeDeactive());
      console.error("Error deleting payment:", error);
      toast(
        error?.response?.data?.message || "Failed to delete payment"
      );
    }
  }, [dispatch, fetchData]);

  const openTreatment = useCallback(() => {
    if (ID || doctorId !== "") {
      setEditTreatmentItem(null);
      setAddTreatmentOpent(true);
    } else {
      toast("select doctor");
    }
  }, [ID, doctorId]);

  const handleEditTreatment = useCallback((item: any) => {
    setEditTreatmentItem(item);
    setAddTreatmentOpent(true);
  }, []);

  const closeTreatmentModal = useCallback(() => {
    setAddTreatmentOpent(false);
    setEditTreatmentItem(null);
  }, []);
  const closePay = useCallback(() => {
    fetchData();
    setPays(false);
  }, [fetchData]);

  const generatePaymentReport = useCallback(() => {
    dispatch(setloadeActive());
    baseUrl
      .get("/client/report/invoiceReport", {
        // .get("/client/report/patientInvoiceReport", {
        responseType: "blob",
        headers: { treatmentid: ID },
      })
      .then((response) => {
        if (response.status === 204) {
          toast("No data found");
          return;
        }
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(pdfBlob);
        link.download = "downloaded.pdf";
        document.body.appendChild(link);

        link.click();

        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      })
      .catch(console.error)
      .finally(() => dispatch(setloadeDeactive()));
  }, [dispatch, ID]);

  return (
    <>
      <div>
        <Container>
          <div className="space-y-3 md:space-y-3">
            <InvoicePatient
              formData={formData}
              setdoctorid={setDoctorId}
              doctorid={doctorId}
              setformData={setFormData}
            />

            {/* Treatment Table Section */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
              <div className="px-4 py-3 md:px-6 md:py-4 border-b border-slate-200 dark:border-slate-600 bg-gradient-to-r from-slate-50 to-white dark:from-slate-700/50 dark:to-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-200">Treatment & Consultation Charges</h3>
                <button
                  onClick={openTreatment}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all w-full sm:w-auto"
                >
                  + ADD
                </button>
              </div>

              {/* Mobile Card View */}
              <div className="block md:hidden divide-y-2 divide-slate-300 dark:divide-slate-500">
                {treatment?.map((item, i) => (
                  <React.Fragment key={item._id ?? i}>
                    {item?.status !== "planned" && (
                      <div className="p-4 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1 truncate">{item.treatment}</h4>
                            <div className="flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-400">
                              <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{item.type}</span>
                              {item.subtype && <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{item.subtype}</span>}
                              {item.toothNo && <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">Tooth: {item.toothNo}</span>}
                              {isLongTermTreatment(item) && (
                                <span className="bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-2 py-0.5 rounded font-medium">
                                  Long Term
                                </span>
                              )}
                            </div>
                          </div>
                          {(canEditInvoiceTreatment(item) || !item?.paid) && (
                            <div className="flex gap-1.5 flex-shrink-0">
                              {canEditInvoiceTreatment(item) && (
                                <button
                                  onClick={() => handleEditTreatment(item)}
                                  className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center hover:bg-amber-100 dark:hover:bg-amber-800/50 transition-colors"
                                  aria-label="Edit treatment"
                                  title={
                                    item?.paid
                                      ? "Edit paid treatment (Super Admin)"
                                      : "Edit treatment"
                                  }
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </button>
                              )}
                              {!item?.paid && (
                                <button
                                  onClick={() => removeDiagnosis(item._id ?? item.treatmentkey)}
                                  className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                                  aria-label="Delete treatment"
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-slate-500 dark:text-slate-400 text-xs">Date</span>
                            <p className="text-slate-700 dark:text-slate-300 font-medium">{item?._id ? item?.createdAt?.split("T")[0] : new Date(item?.createdAt).toLocaleDateString()}</p>
                          </div>
                          {!isLongTermTreatment(item) && (
                            <>
                              <div>
                                <span className="text-slate-500 dark:text-slate-400 text-xs">Qty</span>
                                <p className="text-slate-700 dark:text-slate-300 font-medium">{item.quantity}</p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-slate-500 dark:text-slate-400 text-xs">Price</span>
                                <p className="text-emerald-600 dark:text-emerald-400 font-bold text-lg">{item.price}</p>
                              </div>
                            </>
                          )}
                          {item.note && (
                            <div className="col-span-2">
                              <span className="text-slate-500 dark:text-slate-400 text-xs">Note</span>
                              <p className="text-slate-600 dark:text-slate-400 text-sm">{item.note}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {item?.subTreatment?.map((items: any) => {
                      if (isVisibleSubTreatment(items)) {
                        return (
                          <div key={`${item._id}-${items._id}`} className="p-4 bg-slate-50/50 dark:bg-slate-700/50 hover:bg-slate-100/50 dark:hover:bg-slate-600/50 transition-colors pl-8">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 truncate">{formatSubTreatmentLabel(items)}</h4>
                                <div className="flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-400">
                                  {items.type && <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{items.type}</span>}
                                  {items.subtype && <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{items.subtype}</span>}
                                  {items.toothNo && <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">Tooth: {items.toothNo}</span>}
                                </div>
                              </div>
                              {!items?.paid && (
                                <button
                                  onClick={() => removeSubTreatment({ treatment_id: item._id, sub_treatment_id: items._id })}
                                  className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                                  aria-label="Delete sub treatment"
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-slate-500 dark:text-slate-400 text-xs">Date</span>
                                <p className="text-slate-700 dark:text-slate-300 font-medium">{items?._id ? items?.createdAt?.split("T")[0] : new Date(items?.createdAt).toLocaleDateString()}</p>
                              </div>
                              <div>
                                <span className="text-slate-500 dark:text-slate-400 text-xs">Qty</span>
                                <p className="text-slate-700 dark:text-slate-300 font-medium">{items.quantity || "N/A"}</p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-slate-500 dark:text-slate-400 text-xs">Price</span>
                                <p className="text-emerald-600 dark:text-emerald-400 font-bold text-lg">{items.price || 0}</p>
                              </div>
                              {items.note && (
                                <div className="col-span-2">
                                  <span className="text-slate-500 dark:text-slate-400 text-xs">Note</span>
                                  <p className="text-slate-600 dark:text-slate-400 text-sm">{items.note}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </React.Fragment>
                ))}
                {treatment?.length === 0 && (
                  <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                    <p className="text-sm">No treatments added yet</p>
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block p-4 pt-0">
                <table className="w-full border-collapse border-2 border-slate-400 dark:border-slate-500 [&_th]:border [&_td]:border [&_th]:border-slate-400 [&_td]:border-slate-300 dark:[&_th]:border-slate-500 dark:[&_td]:border-slate-600 [&_tbody_td]:border-b-2">
                  <thead className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Treatment</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider hidden xl:table-cell">Tooth No</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider hidden lg:table-cell">Note</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-28">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-800">
                    {treatment?.map((item, i) => {
                      const visibleSubs = getVisibleSubTreatments(item);
                      const showMainRow = item?.status !== "planned";
                      const lastSubId = visibleSubs[visibleSubs.length - 1]?._id;

                      return (
                      <React.Fragment key={item._id ?? i}>
                        {showMainRow && (
                          <tr
                            className={`transition-colors group hover:bg-sky-50/70 dark:hover:bg-sky-900/20 ${
                              i % 2 === 0
                                ? "bg-white dark:bg-slate-800"
                                : "bg-slate-50 dark:bg-slate-700/30"
                            } ${visibleSubs.length === 0 ? "[&>td]:border-b-[3px] [&>td]:border-slate-400 dark:[&>td]:border-slate-500" : ""}`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                              {item?._id ? item?.createdAt?.split("T")[0] : new Date(item?.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {item.treatment + " " + (item.type ?? "") + " " + (item.subtype ?? "")}
                            </td>
                         
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 hidden xl:table-cell">
                              {item.toothNo}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 text-center font-medium">
                              {isLongTermTreatment(item) ? "—" : item.quantity}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 hidden lg:table-cell max-w-[200px]">
                              <div className="truncate">{item.note || "-"}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-emerald-600 dark:text-emerald-400 text-right">
                              {isLongTermTreatment(item) ? "—" : item.price}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              {(canEditInvoiceTreatment(item) || !item?.paid) && (
                                <div className="inline-flex items-center gap-1">
                                  {canEditInvoiceTreatment(item) && (
                                    <button
                                      onClick={() => handleEditTreatment(item)}
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/50 active:bg-amber-200 transition-all focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800"
                                      aria-label="Edit treatment"
                                      title={
                                        item?.paid
                                          ? "Edit paid treatment (Super Admin)"
                                          : "Edit treatment"
                                      }
                                    >
                                      <EditIcon sx={{ fontSize: 18 }} />
                                    </button>
                                  )}
                                  {!item?.paid && (
                                    <button
                                      onClick={() => removeDiagnosis(item._id ?? item.treatmentkey)}
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800/50 active:bg-red-200 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800"
                                      aria-label="Delete treatment"
                                    >
                                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}

                        {visibleSubs.map((items: any) => {
                          const isLastInGroup = items._id === lastSubId;
                          return (
                              <tr
                                key={`${item._id}-${items._id}`}
                                className={`transition-colors group hover:bg-sky-50/70 dark:hover:bg-sky-900/20 bg-slate-100/90 dark:bg-slate-700/50 ${
                                  isLastInGroup
                                    ? "[&>td]:border-b-[3px] [&>td]:border-slate-400 dark:[&>td]:border-slate-500"
                                    : ""
                                }`}
                              >
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 pl-8">
                                  {items?._id ? items?.createdAt?.split("T")[0] : new Date(items?.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 italic">
                                  └ {formatSubTreatmentLabel(items)}
                                </td>
                          
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 hidden xl:table-cell">
                                  {items.toothNo || "N/A"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 text-center font-medium">{items.quantity || "N/A"}</td>
                                <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 hidden lg:table-cell max-w-[200px]">
                                  <div className="truncate">{items.note || "-"}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-emerald-600 dark:text-emerald-400 text-right">{items.price || 0}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  {!items?.paid && (
                                    <button
                                      onClick={() => removeSubTreatment({ treatment_id: item._id, sub_treatment_id: items._id })}
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800/50 active:bg-red-200 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800"
                                      aria-label="Delete sub treatment"
                                    >
                                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                          );
                        })}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                  <Amount formData={formData} />
                </table>
              </div>
            </div>

            {/* Invoices Section */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
              <div className="px-4 py-3 md:px-6 md:py-4 border-b border-slate-200 dark:border-slate-600 bg-gradient-to-r from-slate-50 to-white dark:from-slate-700/50 dark:to-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-200">Invoices</h3>
                <button
                  onClick={handlePays}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all w-full sm:w-auto"
                >
                  PAY
                </button>
              </div>

              {/* Mobile Card View */}
              <div className="block md:hidden divide-y divide-slate-100 dark:divide-slate-600">
                {payment.map((item, i) => {
                  const isEditable = payment?.length - 1 === i;
                  return (
                    <div key={i} className="p-4 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-bold text-indigo-600 dark:text-indigo-400">#{item?.invoiceNo}</h4>
                            {isEditable && (
                              <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs px-2 py-0.5 rounded-full font-medium">Editable</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{item?.createdAt?.split("T")[0]}</p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => handlePrint({ paymentid: item?._id, invoiceno: item?.invoiceNo })}
                            className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-800/50 transition-colors"
                            title="Print"
                          >
                            <PrintIcon sx={{ fontSize: 18 }} />
                          </button>
                          <button
                            onClick={() => handleSms(item)}
                            disabled={smsDisabled}
                            title={smsDisabled ? SMS_DEPLETED_MESSAGE : "Send SMS"}
                            className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-800/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <SendIcon sx={{ fontSize: 18 }} />
                          </button>
                          {isEditable && (
                            <button
                              onClick={() => handleEdit(item)}
                              className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center hover:bg-amber-100 dark:hover:bg-amber-800/50 transition-colors"
                              title="Edit"
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-500 dark:text-slate-400 text-xs">Total</span>
                          <p className="text-slate-800 dark:text-slate-200 font-bold text-base">{parseInt(item?.netotal) + parseInt(item?.discount)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400 text-xs">Discount</span>
                          <p className="text-orange-600 dark:text-orange-400 font-semibold">{item?.discount}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400 text-xs">Cash</span>
                          <p className="text-emerald-600 dark:text-emerald-400 font-semibold">{item?.cash}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400 text-xs">Card</span>
                          <p className="text-indigo-600 dark:text-indigo-400 font-semibold">{item?.card}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-slate-500 dark:text-slate-400 text-xs">Bank Transfer</span>
                          <p className="text-sky-600 dark:text-sky-400 font-semibold">{item?.bankTransfer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {payment.length === 0 && (
                  <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                    <p className="text-sm">No invoices yet</p>
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-700/50 dark:to-slate-700 border-b-2 border-slate-200 dark:border-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Invoice No</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Discount</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Cash</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider hidden lg:table-cell">Card</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider hidden lg:table-cell">Transfer</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-32">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-600">
                    {payment.map((item, i) => (
                      <PaymentTr
                        item={item}
                        key={i}
                        handlePrint={handlePrint}
                        handleSms={handleSms}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        editable={payment?.length - 1 === i}
                        smsDisabled={smsDisabled}
                        smsDisabledMessage={SMS_DEPLETED_MESSAGE}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Report Buttons */}
            {ID && (
              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <button
                  onClick={() => setReportOpen(true)}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-primary to-primaryLight text-white text-xs sm:text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all w-full sm:w-auto"
                >
                  General Report
                </button>
                <button
                  onClick={generatePaymentReport}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-primary to-primaryLight text-white text-xs sm:text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all w-full sm:w-auto"
                >
                  Payment Report
                </button>
              </div>
            )}
          </div>

          {addTreatmentOpent && (
            <InvoiceTreatment
              treatment={treatment}
              setaddTreatmentOpent={closeTreatmentModal}
              settreatment={setTreatment}
              addToTreatment={addToTreatment}
              doctorid={doctorId}
              editItem={editTreatmentItem}
            />
          )}
          {pays && (
            <Pays
              handleSave={handleSave}
              closePay={closePay}
              formData={formData}
              setformData={setFormData}
              edit={edit}
              smsDisabled={smsDisabled}
              smsDisabledMessage={SMS_DEPLETED_MESSAGE}
            />
          )}
        </Container>
        {reportOpen && <ReportModal setReportOpen={setReportOpen} />}
      </div>
      <div style={{ position: "absolute", left: "-9999px" }}>
        <div ref={printRef} style={{ display: "none" }}>
          {printTemplate === "printTemplate_2" ? (
            <Printtest stateKey="printTemplate2" />
          ) : (
            <Printtest />
          )}
        </div>
      </div>
    </>
  );
};

export default Invoice;
